<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  prompt: {},
  category: String,
})

const currentValid = computed(() => ({
  site: !!props.prompt.currentEntry?.result?.answered,
  openai: !!(props.prompt.currentEntry?.openai_result?.answered && props.prompt.currentEntry?.openai_result?.verification?.correct),
  gemini: props.prompt.currentEntry?.gemini_status === 'skipped' || !!(props.prompt.currentEntry?.gemini_result?.answered && props.prompt.currentEntry?.gemini_result?.verification?.correct)
}))
const oldValid = computed(() => ({
  site: !!props.prompt.oldEntry?.result?.answered,
  openai: !!(props.prompt.oldEntry?.openai_result?.answered && props.prompt.oldEntry?.openai_result?.verification?.correct),
  gemini: props.prompt.oldEntry?.gemini_status === 'skipped' || !!(props.prompt.oldEntry?.gemini_result?.answered && props.prompt.oldEntry?.gemini_result?.verification?.correct)
}))

const siteChanged = computed(() => currentValid.value.site !== oldValid.value.site)
const openaiChanged = computed(() => currentValid.value.openai !== oldValid.value.openai)
const geminiChanged = computed(() => currentValid.value.gemini !== oldValid.value.gemini)

const anyChanged = computed(() => siteChanged.value || openaiChanged.value || geminiChanged.value)

const displaySource = computed(() => {
  if (siteChanged.value) return 'site'
  if (openaiChanged.value) return 'openai'
  if (geminiChanged.value) return 'gemini'

  // Fallback to highest priority valid source
  if (currentValid.value.site) return 'site'
  if (currentValid.value.openai) return 'openai'
  if (currentValid.value.gemini) return 'gemini'
  return 'site'
})

const getEntryDisplay = () => {
  let oldEntry = props.prompt.oldEntry;
  let currentEntry = props.prompt.currentEntry;

  let oldResult = {
    'source': 'Site',
    'answer': '',
    'reason': '',
    'answered': false,
  }
  let currentResult = {
    'source': 'Site',
    'answer': '',
    'reason': '',
    'answered': false,
  }

  if (props.category === 'updated_answer') {
    oldResult = {
      'source': 'Site',
      'answer': oldEntry?.result?.answer,
      'reason': '',
      'answered': oldEntry?.result?.answered,
    }
    currentResult = {
      'source': 'Site',
      'answer': currentEntry?.result?.answer,
      'reason': currentEntry?.result?.verification.old_answer_error_reason,
      'answered': currentEntry?.result?.answered,
    }
  }
  else if (props.category === 'answered_and_verified') {
    oldResult = null;
    currentResult = {
      'source': 'Site',
      'answer': currentEntry?.result?.answer,
      'reason': currentEntry?.result?.answer_reason,
      'answered': currentEntry?.result?.answered,
    }
  }
  else if (props.category === 'llm_updated_answer') {
    if (!currentEntry.openai_result?.verification?.old_answer_correct) {
      oldResult = {
        'source': 'ChatGPT',
        'answer': oldEntry?.openai_result?.answer,
        'reason': oldEntry?.openai_result?.answer,
        'answered': oldEntry?.openai_result?.answered,
      }
      currentResult = {
        'source': 'ChatGPT',
        'answer': currentEntry?.openai_result?.answer,
        'reason': currentEntry?.openai_result?.verification.old_answer_error_reason,
        'answered': currentEntry?.openai_result?.answered,
      }
    } else if (!currentEntry.gemini_result?.verification?.old_answer_correct) {
      oldResult = {
        'source': 'Gemini',
        'answer': oldEntry?.gemini_result?.answer,
        'reason': oldEntry?.gemini_result?.answer,
        'answered': oldEntry?.gemini_result?.answered,
      }
      currentResult = {
        'source': 'Gemini',
        'answer': currentEntry?.gemini_result?.answer,
        'reason': currentEntry?.gemini_result?.verification.old_answer_error_reason,
        'answered': currentEntry?.gemini_result?.answered,
      }
    }
  }
  else if (props.category === 'llm_not_answered') {
    if (!currentEntry.openai_result?.answered) {
      oldResult = {
        'source': 'ChatGPT',
        'answer': oldEntry?.openai_result?.answer,
        'reason': oldEntry?.openai_result?.answer,
        'answered': oldEntry?.openai_result?.answered,
      }
      currentResult = {
        'source': 'ChatGPT',
        'answer': currentEntry?.openai_result?.answer,
        'reason': currentEntry?.openai_result?.answer_reason,
        'answered': currentEntry?.openai_result?.answered,
      }
    } else if (!currentEntry.gemini_result?.answered) {
      oldResult = {
        'source': 'Gemini',
        'answer': oldEntry?.gemini_result?.answer,
        'reason': oldEntry?.gemini_result?.answer,
        'answered': oldEntry?.gemini_result?.answered,
      }
      currentResult = {
        'source': 'Gemini',
        'answer': currentEntry?.gemini_result?.answer,
        'reason': currentEntry?.gemini_result?.verification.old_answer_error_reason,
        'answered': currentEntry?.gemini_result?.answered,
      }
    }
  }
  else if (props.category === 'llm_not_verified') {
    if (!currentEntry.openai_result?.verification?.correct) {
      oldResult = {
        'source': 'ChatGPT',
        'answer': oldEntry?.openai_result?.answer,
        'reason': oldEntry?.openai_result?.answer,
        'answered': oldEntry?.openai_result?.answered,
      }
      currentResult = {
        'source': 'ChatGPT',
        'answer': currentEntry?.openai_result?.answer,
        'reason': currentEntry?.openai_result?.answer_reason,
        'answered': currentEntry?.openai_result?.answered,
      }
    } else if (!currentEntry.gemini_result?.verification?.correct) {
      oldResult = {
        'source': 'Gemini',
        'answer': oldEntry?.gemini_result?.answer,
        'reason': oldEntry?.gemini_result?.answer,
        'answered': oldEntry?.gemini_result?.answered,
      }
      currentResult = {
        'source': 'Gemini',
        'answer': currentEntry?.gemini_result?.answer,
        'reason': currentEntry?.gemini_result?.verification.old_answer_error_reason,
        'answered': currentEntry?.gemini_result?.answered,
      }
    }
  }

  return {
    old: oldResult,
    current: currentResult
  }

}

const currentDisplay = computed(() => getEntryDisplay().current)
const oldDisplay = computed(() => getEntryDisplay().old)

const showFullAnswerOld = ref(false);
const showFullReasonOld = ref(false);
const showFullAnswerNew = ref(false);
const showFullReasonNew = ref(false);

function shouldTruncate(text, limit = 150) {
  return text && text.length > limit;
}
</script>

<template>
  <div class="history-mark border-start bg-light border-2 border-secondary-subtle mb-4">
    <div class="mb-3 py-2 px-3 ">
      <div class="fw-bold text-dark" style="font-size: 0.9rem; line-height: 1.4;">{{
          prompt.question
        }}
      </div>
    </div>

    <div class="d-flex flex-column gap-2">
      <!-- New Answer Block -->

      <div class="p-2 rounded bg-white border shadow-sm">
        <div class="d-flex align-items-center gap-2 mb-1">
                            <span class="badge bg-primary"
                                  style="font-size: 0.6rem;">{{ $filters.dateFilter(prompt.dateNew) }}</span>
          <small class="text-primary fw-bold" style="font-size: 0.7rem;">NEW ANSWER</small>
        </div>

        <div class="d-flex flex-column gap-2 mt-2">
          <div class="fw-medium small">
            <strong>{{ currentDisplay.source }}: </strong>
            <template v-if="showFullAnswerNew || !shouldTruncate(currentDisplay.answer)">
              {{ currentDisplay.answer || 'No answer' }}
            </template>
            <template v-else>
              {{ currentDisplay.answer.substring(0, 150) }}...
            </template>
            <button
                v-if="shouldTruncate(currentDisplay.answer)"
                class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                style="font-size: 0.7rem; text-decoration: none;"
                @click.stop="showFullAnswerNew = !showFullAnswerNew"
            >
              {{ showFullAnswerNew ? 'Show less' : 'Read more' }}
            </button>
          </div>

          <div v-if="currentDisplay.reason"
               class="mt-1 p-2 rounded bg-light border-start border-3 border-primary border-opacity-25"
               style="font-size: 0.7rem;">
            <span class="text-muted italic">
              <template v-if="showFullReasonNew || !shouldTruncate(currentDisplay.reason, 100)">
                {{ currentDisplay.reason }}
              </template>
              <template v-else>
                {{ currentDisplay.reason.substring(0, 100) }}...
              </template>
              <button
                  v-if="shouldTruncate(currentDisplay.reason, 100)"
                  class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                  style="font-size: 0.7rem; text-decoration: none;"
                  @click.stop="showFullReasonNew = !showFullReasonNew"
              >
                {{ showFullReasonNew ? 'Show less' : 'Read more' }}
              </button>
            </span>
          </div>
        </div>
      </div>

      <!-- Old Answer Block -->
      <div v-if="oldDisplay"
          class="p-2 rounded bg-light border">
        <div class="d-flex align-items-center gap-2 mb-1">
                            <span class="badge bg-secondary opacity-75"
                                  style="font-size: 0.6rem;">{{ $filters.dateFilter(prompt.dateOld) }}</span>
          <small class="text-muted fw-bold" style="font-size: 0.7rem;">PREVIOUS ANSWER</small>
        </div>

        <div class="d-flex flex-column gap-2 mt-2">
          <div class="text-muted text-decoration-line-through small italic">
            <strong>{{ oldDisplay.source }}: </strong>
            <template v-if="showFullAnswerOld || !shouldTruncate(oldDisplay.answer)">
              {{ oldDisplay.answer || 'No answer' }}
            </template>
            <template v-else>
              {{ oldDisplay.answer.substring(0, 150) }}...
            </template>
            <button
                v-if="shouldTruncate(oldDisplay.answer)"
                class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                style="font-size: 0.7rem; text-decoration: none;"
                @click.stop="showFullAnswerOld = !showFullAnswerOld"
            >
              {{ showFullAnswerOld ? 'Show less' : 'Read more' }}
            </button>
          </div>

          <div v-if="oldDisplay.reason"
               class="mt-1 p-2 rounded bg-light border-start border-3 border-secondary border-opacity-25"
               style="font-size: 0.7rem;">
            <span class="text-muted italic">
              <template v-if="showFullReasonOld || !shouldTruncate(oldDisplay.reason, 100)">
                {{ oldDisplay.reason }}
              </template>
              <template v-else>
                {{ oldDisplay.reason.substring(0, 100) }}...
              </template>
              <button
                  v-if="shouldTruncate(oldDisplay.reason, 100)"
                  class="btn btn-link btn-xs p-0 ms-1 text-primary fw-medium"
                  style="font-size: 0.7rem; text-decoration: none;"
                  @click.stop="showFullReasonOld = !showFullReasonOld"
              >
                {{ showFullReasonOld ? 'Show less' : 'Read more' }}
              </button>
            </span>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>
<style scoped>
.btn-xs {
  padding: 0.1rem 0.4rem;
  font-size: 0.75rem;
}

.italic {
  font-style: italic;
}
</style>
